import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, taskAPI, taskStatusAPI, taskRoleAPI, companyAPI, leaveAPI, requirementAPI } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

import TaskDetailModal from './TaskDetailModal';
import AssigneeModal from './AssigneeModal';
import InlineTaskCreator from './InlineTaskCreator';
import ListView from './views/ListView';
import BoardView from './views/BoardView';
import CalendarView from './views/CalendarView';
import { autoScheduleAllTasks } from '../../utils/ganttScheduler';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import GenerateAITasksModal from './GenerateAITasksModal';

const TasksTab = ({ projectId, project: initialProject, users: initialUsers, onRefresh: onProjectRefresh }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { state: authState } = useAuth();
    const user = authState?.user;
    const [project, setProject] = useState(initialProject || null);
    const [company, setCompany] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [teamActivity, setTeamActivity] = useState([]);
    const [users, setUsers] = useState(initialUsers || []);
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [sprints, setSprints] = useState([]);
    const [phases, setPhases] = useState([]);
    const [taskRoles, setTaskRoles] = useState([]);
    const [requirements, setRequirements] = useState([]);
    const [meetingNotes, setMeetingNotes] = useState([]);
    const [employeeLeaves, setEmployeeLeaves] = useState([]);
    const [taskCosts, setTaskCosts] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedSprint] = useState('');
    const [selectedPhase] = useState('');
    const [currentView, setCurrentView] = useState(() => {
        try {
            return localStorage.getItem(`project_task_view_${id}`) || 'list';
        } catch (e) {
            return 'list';
        }
    });

    const handleSetCurrentView = (view) => {
        setCurrentView(view);
        try {
            localStorage.setItem(`project_task_view_${id}`, view);
        } catch (e) {}
    };
    const [showInlineCreator, setShowInlineCreator] = useState(false);
    const [showAITasksModal, setShowAITasksModal] = useState(false);

    // Filter states
    const [selectedAssignee, setSelectedAssignee] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedTaskRole, setSelectedTaskRole] = useState('');
    const [selectedProjectRole, setSelectedProjectRole] = useState('');
    const [dueDateFrom, setDueDateFrom] = useState('');
    const [dueDateTo, setDueDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Auto-schedule states
    const [showAutoScheduleModal, setShowAutoScheduleModal] = useState(false);
    const [isAutoScheduling, setIsAutoScheduling] = useState(false);
    const [scheduleRoleId, setScheduleRoleId] = useState('');
    const [scheduleStartDate, setScheduleStartDate] = useState(() => new Date().toISOString().split('T')[0]);

    // Duration entry states
    const [isDurationEntryMode, setIsDurationEntryMode] = useState(false);
    const [durationContext, setDurationContext] = useState({ roleId: '', userId: '' });
    const [pendingDurations, setPendingDurations] = useState({});
    const [existingDurations, setExistingDurations] = useState({});
    const [isSubmittingDurations, setIsSubmittingDurations] = useState(false);
    const [deleteTaskModal, setDeleteTaskModal] = useState({ isOpen: false, taskId: null, taskTitle: '' });
    const [assigneeModalTask, setAssigneeModalTask] = useState(null);
    const [myDurations, setMyDurations] = useState({});

    const isProjectManager = React.useMemo(() => {
        if (!user || !project) return false;
        return (project.projectManager?._id || project.projectManager) === user?._id;
    }, [user, project]);

    const canViewCost = React.useMemo(() => {
        if (!user || !project) return false;
        if (user.role === 'superadmin' || user.role === 'owner' || user.role === 'company_admin' || user.role === 'admin') return true;
        
        // Project Manager also has cost/duration visibility
        if (isProjectManager) return true;

        if (project.owner === user._id || project.owner?._id === user._id) return true;
        if (user.permissions && user.permissions.includes('viewProjectCost')) return true;
        return false;
    }, [user, project, isProjectManager]);

    useEffect(() => {
        if (isDurationEntryMode && durationContext.roleId && durationContext.userId) {
            taskAPI.getBulkUserDurations(id, durationContext.userId, durationContext.roleId)
                .then(res => {
                    setExistingDurations(res.data || {});
                    setPendingDurations({});
                })
                .catch(() => setExistingDurations({}));
        } else {
            setExistingDurations({});
        }
    }, [isDurationEntryMode, durationContext.roleId, durationContext.userId, id]);



    const fetchTeamActivity = React.useCallback(async () => {
        try {
            const activityRes = await api.get('/team-activity', { params: { projectId: id } });
            setTeamActivity(activityRes.data || []);
        } catch (err) {
            console.error('Failed to sync activity', err);
        }
    }, [id]);

    const fetchProjectData = React.useCallback(async () => {
        try {
            setLoading(true);
            const [tasksRes, statusesRes, rolesRes, activityRes, reqsRes] = await Promise.all([
                taskAPI.getAll(id).catch(() => ({ data: [] })),
                taskStatusAPI.getAll(id).catch(() => ({ data: [] })),
                taskRoleAPI.getAll(id).catch(() => ({ data: [] })),
                api.get('/team-activity', { params: { projectId: id } }).catch(() => ({ data: [] })),
                requirementAPI.getAll(id).catch(() => ({ data: [] }))
            ]);

            setTasks(tasksRes.data);
            setTeamActivity(activityRes.data || []);
            setTaskStatuses(statusesRes.data);
            setTaskRoles(rolesRes.data || []);
            setRequirements(reqsRes.data || []);
            
            // Sprints, phases and meeting notes can be fetched on-demand when modals open,
            // or left empty if they aren't critical for the initial view.
            setSprints([]);
            setPhases([]);
            setMeetingNotes([]);

            try {
                const costsRes = await projectAPI.getCosts(id);
                if (costsRes.data?.tasks) {
                    const costsMap = {};
                    costsRes.data.tasks.forEach(t => costsMap[t._id] = t.totalCost);
                    setTaskCosts(costsMap);
                }
            } catch (e) {
                console.error('Failed to load costs', e);
            }

            if (project?.company) {
                const companyId = project.company._id || project.company;
                companyAPI.getById(companyId).then(res => setCompany(res.data)).catch(console.error);
                leaveAPI.getAll(companyId, { status: 'approved' }).then(res => setEmployeeLeaves(res.data.leaves || [])).catch(console.error);
            }

            if (user?._id) {
                taskAPI.getBulkUserDurations(id, user._id, '')
                    .then(res => setMyDurations(res.data || {}))
                    .catch(e => console.error('Failed to load personal durations', e));
            }

        } catch (error) {
            console.error('Failed to load project data', error);
        } finally {
            setLoading(false);
        }
    }, [id, user?._id, project?.company]);

    useEffect(() => {
        if (initialProject) setProject(initialProject);
    }, [initialProject]);

    useEffect(() => {
        const projectTeam = [];
        if (initialProject?.owner) projectTeam.push({ ...initialProject.owner, projectRole: 'Owner' });
        initialProject?.members?.forEach(m => {
            if (m.user && !projectTeam.find(u => u._id === m.user._id)) {
                projectTeam.push({ ...m.user, projectRole: m.role || 'Member' });
            }
        });
        setUsers(projectTeam.length > 0 ? projectTeam : initialUsers);
    }, [initialProject, initialUsers]);

    const fetchedIdRef = React.useRef(null);

    useEffect(() => {
        if (fetchedIdRef.current === id) return;
        fetchedIdRef.current = id;
        
        fetchProjectData();
        const intervalId = setInterval(fetchTeamActivity, 30000);
        return () => clearInterval(intervalId);
    }, [id, fetchProjectData, fetchTeamActivity]);

    const handleCreateTask = async (taskData) => {
        try {
            const data = { ...taskData, sprint: selectedSprint || undefined, phase: selectedPhase || undefined };
            if (!data.status) data.status = project?.settings?.defaultTaskStatus || taskStatuses[0]?._id;
            const maxOrder = tasks.length > 0 ? Math.max(...tasks.map(t => t.order || 0)) : 0;
            data.order = maxOrder + 1;
            await taskAPI.create(id, data);
            fetchProjectData();
            if (onProjectRefresh) onProjectRefresh();
        } catch (error) {
            console.error('Failed to create task', error);
        }
    };

    const handleGenerateAITasksSubmit = async (generatedTasks) => {
        try {
            const maxOrder = tasks.length > 0 ? Math.max(...tasks.map(t => t.order || 0)) : 0;
            const defaultStatus = project?.settings?.defaultTaskStatus || taskStatuses[0]?._id;
            
            // We use Promise.all to submit all tasks to the backend at once.
            // If the array is very large, consider a chunked approach, but for 5-10 tasks this is fine.
            const promises = generatedTasks.map((t, index) => {
                const data = {
                    title: t.title,
                    description: t.description,
                    sprint: selectedSprint || undefined,
                    phase: selectedPhase || undefined,
                    status: defaultStatus,
                    order: maxOrder + index + 1
                };
                return taskAPI.create(id, data);
            });
            
            await Promise.all(promises);
            fetchProjectData();
            if (onProjectRefresh) onProjectRefresh();
        } catch (error) {
            console.error('Failed to create bulk tasks', error);
            alert('Failed to submit all tasks.');
        }
    };

    const handleUpdateTask = async (taskId, updates, skipFetch = false) => {
        try {
            const response = await taskAPI.update(id, taskId, updates);
            if (selectedTask?._id === taskId) setSelectedTask(response.data);
            if (!skipFetch) fetchProjectData();
            if (onProjectRefresh) onProjectRefresh();
        } catch (error) {
            console.error('Failed to update task', error);
        }
    };

    const handleUpdateTaskStatus = async (taskId, statusId) => {
        const newStatusObj = taskStatuses.find(s => s._id === statusId) || (statusId ? { _id: statusId } : null);
        setTasks(prevTasks => prevTasks.map(t => {
            if (t._id === taskId) {
                return { ...t, status: newStatusObj };
            }
            return t;
        }));

        try {
            await taskAPI.update(id, taskId, { status: statusId });
        } catch (error) {
            console.error('Failed to update task status', error);
            fetchProjectData();
        }
    };

    const handleReorderTasks = async (newTasks) => {
        try {
            setTasks(newTasks);
            const taskOrders = newTasks.map((t, index) => ({ taskId: t._id, order: index + 1 }));
            await taskAPI.reorder(id, taskOrders);
        } catch (error) {
            console.error('Failed to reorder tasks', error);
            fetchProjectData();
        }
    };

    const handleSubmitDurations = async () => {
        if (!durationContext.roleId) return;
        setIsSubmittingDurations(true);
        try {
            const updates = Object.entries(pendingDurations).map(([taskId, value]) => ({
                taskId,
                duration: parseFloat(value) // backend parses this directly as hours
            }));
            await taskAPI.bulkUpdateRoleDurations(id, durationContext.roleId, updates, durationContext.userId);
            setIsDurationEntryMode(false);
            setPendingDurations({});
            fetchProjectData();
        } catch (error) {
            console.error('Failed to submit durations', error);
        } finally {
            setIsSubmittingDurations(false);
        }
    };

    const handleSelectTask = (task) => {
        navigate(`/projects/${id}/tasks/${task._id}`);
    };

    const handleOpenAssigneeModal = (task) => {
        setAssigneeModalTask(task);
    };

    const handleDeleteTask = async () => {
        try {
            await taskAPI.delete(id, deleteTaskModal.taskId);
            if (selectedTask?._id === deleteTaskModal.taskId) setSelectedTask(null);
            fetchProjectData();
            if (onProjectRefresh) onProjectRefresh();
        } catch (e) { console.error('Failed to delete task', e); }
        finally { setDeleteTaskModal({ isOpen: false, taskId: null, taskTitle: '' }); }
    };

    const handleSetMyDuration = async (taskId, hours) => {
        try {
            const numHours = parseFloat(hours) || 0;
            await taskAPI.setRoleDuration(id, taskId, { targetUserId: user._id, durationMinutes: Math.round(numHours * 60) });
            setMyDurations(prev => ({ ...prev, [taskId]: Math.round(numHours * 60) }));
        } catch (e) { console.error('Failed to set personal duration', e); }
    };

    const handleAutoSchedule = async () => {
        if (!scheduleStartDate || !scheduleRoleId) return;

        setIsAutoScheduling(true);
        try {
            const settings = {
                workingDays: company?.settings?.workingDays || project?.settings?.workingDays || [1, 2, 3, 4, 5],
                holidays: company?.settings?.holidays || project?.settings?.holidays || [],
                timeTracking: company?.settings?.timeTracking || project?.settings?.timeTracking || { hoursPerDay: 8, daysPerWeek: 5, workingHoursStart: '09:00', workingHoursEnd: '17:00' }
            };

            const tasksOrdered = [...tasks].sort((a, b) => (a.order || 0) - (b.order || 0));
            const startIso = new Date(scheduleStartDate + 'T00:00:00').toISOString();

            let durationOverrides = {};
            try {
                const res = await taskAPI.getRoleTaskDurations(id, scheduleRoleId);
                durationOverrides = res.data || {};
            } catch (err) {
                console.warn('Could not fetch role task durations, falling back to role-assignment durations.', err);
            }

            const schedules = autoScheduleAllTasks(tasksOrdered, startIso, settings, employeeLeaves, { roleId: scheduleRoleId, durationOverrides });

            if (schedules.length === 0) {
                console.warn('Auto-schedule: no schedulable tasks found.', { durationOverrides });
                alert('No schedulable tasks were found for this role. Make sure the selected role is assigned to tasks and that durations have been recorded.');
                return;
            }

            await taskAPI.bulkSchedule(id, schedules);
            setShowAutoScheduleModal(false);
            fetchProjectData();
        } catch (e) {
            console.error('Auto-schedule failed', e);
            alert('Auto-schedule failed: ' + (e?.response?.data?.error || e.message || 'unknown error'));
        }
        finally { setIsAutoScheduling(false); }
    };

    // When duration entry mode is active with a role selected, only show tasks that have that role
    const durationRoleId = isDurationEntryMode ? durationContext.roleId : '';

    // Users assigned to the selected duration role (for the User dropdown in duration mode)
    const durationRoleUsers = React.useMemo(() => {
        if (!durationRoleId || !project?.members) return users;
        const selectedRoleObj = taskRoles.find(r => r._id === durationRoleId);
        if (!selectedRoleObj) return users;
        
        const roleNameBase = selectedRoleObj.name.toLowerCase();
        
        // Filter members who have this role name in their project role designation
        const memberIds = project.members
            .filter(m => (m.role || '').toLowerCase().includes(roleNameBase))
            .map(m => (m.user?._id || m.user).toString());
            
        return users.filter(u => memberIds.includes(u._id.toString()));
    }, [durationRoleId, project?.members, taskRoles, users]);


    const filteredTasks = tasks.filter(task => {
        if (selectedSprint && task.sprint?._id !== selectedSprint) return false;
        if (selectedPhase && task.phase?._id !== selectedPhase) return false;
        // Auto-filter by duration role when in bulk entry mode
        if (durationRoleId && !task.roleAssignments?.some(ra => (ra.role?._id || ra.role) === durationRoleId)) return false;
        if (selectedTaskRole && !task.roleAssignments?.some(ra => (ra.role?._id || ra.role) === selectedTaskRole)) return false;
        if (selectedProjectRole) {
            const ids = users.filter(u => u.projectRole === selectedProjectRole).map(u => u._id);
            if (!task.assignees?.some(a => ids.includes(a._id || a)) && !task.roleAssignments?.some(ra => ra.assignees?.some(a => ids.includes(a._id || a)))) return false;
        }
        if (selectedAssignee) {
            if (!task.assignees?.some(a => (a._id || a) === selectedAssignee) && !task.roleAssignments?.some(ra => ra.assignees?.some(a => (a._id || a) === selectedAssignee))) return false;
        }
        if (selectedPriority && task.priority !== selectedPriority) return false;
        if (selectedStatus && task.status?._id !== selectedStatus) return false;
        if (dueDateFrom && new Date(task.dueDate) < new Date(dueDateFrom)) return false;
        if (dueDateTo && new Date(task.dueDate) > new Date(dueDateTo + 'T23:59:59')) return false;
        return true;
    });

    const activeFilterCount = [selectedAssignee, selectedPriority, selectedStatus, selectedTaskRole, selectedProjectRole, dueDateFrom, dueDateTo].filter(Boolean).length;

    if (loading) return (
        <div className="p-16 text-center space-y-4">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin mx-auto" />
            <p className="text-sm font-bold text-slate-400">Loading tasks...</p>
        </div>
    );

    return (
        <div className="flex flex-col min-h-[500px] space-y-6">
            {/* Toolbar */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
                <div className="flex bg-slate-50 p-1 rounded-xl gap-1 border border-slate-100 shrink-0">
                    {[
                        { id: 'list', label: 'List', icon: '☰' },
                        { id: 'board', label: 'Board', icon: '⚏' },
                        { id: 'calendar', label: 'Timeline', icon: '📅' }
                    ].map(view => (
                        <button
                            key={view.id}
                            onClick={() => handleSetCurrentView(view.id)}
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${currentView === view.id ? 'bg-white text-indigo-600 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                            <span>{view.icon}</span>
                            {view.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-4 py-2 rounded-xl border text-xs font-bold flex items-center gap-2 transition-all ${showFilters || activeFilterCount > 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                        <span>🔍</span>
                        Filters {activeFilterCount > 0 && <span className="bg-indigo-600 text-white px-1.5 py-0.5 rounded-full text-[9px]">{activeFilterCount}</span>}
                    </button>

                    {canViewCost && (
                        <button
                            onClick={() => {
                                const enteringMode = !isDurationEntryMode;
                                setIsDurationEntryMode(enteringMode);
                                if (enteringMode) {
                                    setDurationContext({ roleId: '', userId: user?._id || '' });
                                }
                            }}
                            className={`px-4 py-2 rounded-xl border text-xs font-bold flex items-center gap-2 transition-all ${isDurationEntryMode ? 'bg-amber-500 border-amber-600 text-white shadow-md' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                        >
                            <span>⏱️</span>
                            Durations
                        </button>
                    )}

                    <button
                        onClick={() => setShowAutoScheduleModal(true)}
                        className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-indigo-100 transition-colors"
                    >
                        <span>⚡</span>
                        Auto Schedule
                    </button>

                    <button
                        onClick={() => setShowAITasksModal(true)}
                        className="px-4 py-2 bg-purple-50 text-purple-600 border border-purple-100 rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-purple-100 transition-colors shadow-sm"
                    >
                        <span>✨</span>
                        Generate AI Tasks
                    </button>

                    <button
                        onClick={() => setShowInlineCreator(true)}
                        className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-sm"
                    >
                        <span>+</span>
                        Add Task
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 animate-in slide-in-from-top-2">
                    <div className="col-span-full flex justify-end">
                        <button onClick={() => { setSelectedAssignee(''); setSelectedPriority(''); setSelectedStatus(''); setSelectedTaskRole(''); setSelectedProjectRole(''); setDueDateFrom(''); setDueDateTo(''); }} className="text-[10px] font-bold text-rose-600 uppercase tracking-wider hover:underline">Reset Filters</button>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Assignee</label>
                        <select value={selectedAssignee} onChange={e => setSelectedAssignee(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none focus:border-indigo-400">
                            <option value="">All Assignees</option>
                            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Status</label>
                        <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none">
                            <option value="">All Statuses</option>
                            {taskStatuses.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Priority</label>
                        <select value={selectedPriority} onChange={e => setSelectedPriority(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none">
                            <option value="">All Priorities</option>
                            {['urgent', 'high', 'medium', 'low'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Due From</label>
                        <input type="date" value={dueDateFrom} onChange={e => setDueDateFrom(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none" />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Due To</label>
                        <input type="date" value={dueDateTo} onChange={e => setDueDateTo(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none" />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Workflow Role</label>
                        <select value={selectedTaskRole} onChange={e => setSelectedTaskRole(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none">
                            <option value="">All Roles</option>
                            {taskRoles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {/* Duration Mode Sub-Bar */}
            {isDurationEntryMode && (
                <div className="bg-amber-500 p-4 rounded-2xl text-white shadow-md flex items-center justify-between animate-in zoom-in-95">
                    <div className="flex items-center gap-6">
                        <div className="text-[10px] font-bold bg-white/20 px-3 py-1 rounded-lg">BULK DURATION ENTRY</div>
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold uppercase opacity-80">Role:</span>
                            <select
                                value={durationContext.roleId}
                                onChange={e => {
                                    const newRoleId = e.target.value;
                                    // Find members who belong to this role
                                    let newUserId = '';
                                    if (newRoleId && project?.members) {
                                        const selectedRoleObj = taskRoles.find(r => r._id === newRoleId);
                                        if (selectedRoleObj) {
                                            const roleNameBase = selectedRoleObj.name.toLowerCase();
                                            const memberIds = project.members
                                                .filter(m => (m.role || '').toLowerCase().includes(roleNameBase))
                                                .map(m => (m.user?._id || m.user).toString());
                                            const roleUsers = users.filter(u => memberIds.includes(u._id.toString()));
                                            // Auto-select if only one user
                                            if (roleUsers.length === 1) {
                                                newUserId = roleUsers[0]._id;
                                            }
                                        }
                                    }
                                    setDurationContext({ roleId: newRoleId, userId: newUserId });
                                    // Fetch durations when we have both role and user
                                    if (newRoleId && newUserId) {
                                        taskAPI.getBulkUserDurations(id, newUserId, newRoleId)
                                            .then(res => {
                                                setPendingDurations({});
                                                setExistingDurations(res.data || {});
                                            })
                                            .catch(err => { console.error('Duration fetch error:', err); });
                                    } else {
                                        setPendingDurations({});
                                        setExistingDurations({});
                                    }
                                }}
                                className="bg-white/10 border-white/20 rounded-lg px-3 py-1.5 text-xs font-bold outline-none cursor-pointer"
                            >
                                <option value="" className="text-slate-800">Select Role</option>
                                {taskRoles.map(r => <option key={r._id} value={r._id} className="text-slate-800">{r.name}</option>)}
                            </select>
                        </div>
                        {canViewCost && (
                            <div className="flex items-center gap-3">
                                <span className="text-[10px] font-bold uppercase opacity-80">User:</span>
                                <select
                                    value={durationContext.userId || ''}
                                    onChange={e => {
                                        const newUserId = e.target.value;
                                        setDurationContext(prev => ({ ...prev, userId: newUserId }));
                                        // Fetch durations immediately
                                        if (newUserId && durationContext.roleId) {
                                            taskAPI.getBulkUserDurations(id, newUserId, durationContext.roleId)
                                                .then(res => {
                                                    setPendingDurations({});
                                                    setExistingDurations(res.data || {});
                                                })
                                                .catch(err => { console.error('Duration fetch error:', err); });
                                        } else {
                                            setPendingDurations({});
                                            setExistingDurations({});
                                        }
                                    }}
                                    className="bg-white/10 border-white/20 rounded-lg px-3 py-1.5 text-xs font-bold outline-none cursor-pointer"
                                >
                                    <option value="" className="text-slate-800">{durationRoleId && durationRoleUsers.length === 0 ? 'No users in role' : 'Select User'}</option>
                                    {durationRoleUsers.map(u => <option key={u._id} value={u._id} className="text-slate-800">{u.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsDurationEntryMode(false)} className="px-4 py-1.5 text-xs font-bold hover:bg-white/10 rounded-lg transition-colors">Cancel</button>
                        <button onClick={handleSubmitDurations} disabled={isSubmittingDurations} className="bg-white text-amber-600 px-6 py-1.5 rounded-lg text-xs font-bold shadow-sm hover:scale-105 transition-all disabled:opacity-50">
                            {isSubmittingDurations ? 'Saving...' : 'Save Durations'}
                        </button>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px]">
                {currentView === 'list' && (
                    <ListView
                        tasks={filteredTasks}
                        onEditTask={handleSelectTask}
                        onDeleteTask={t => setDeleteTaskModal({ isOpen: true, taskId: t._id, taskTitle: t.title })}
                        onOpenAssigneeModal={handleOpenAssigneeModal}
                        onSelectTask={handleSelectTask}
                        onReorderTasks={handleReorderTasks}
                        taskCosts={taskCosts}
                        teamActivity={teamActivity}
                        isDurationEntryMode={isDurationEntryMode}
                        durationContext={durationContext}
                        pendingDurations={pendingDurations}
                        setPendingDurations={setPendingDurations}
                        existingDurations={existingDurations}
                        canViewCost={canViewCost}
                        myDurations={myDurations}
                        onSetMyDuration={handleSetMyDuration}
                        currentUserId={user?._id}
                    />
                )}
                {currentView === 'board' && (
                    <BoardView
                        tasks={filteredTasks}
                        taskStatuses={taskStatuses}
                        onUpdateTaskStatus={handleUpdateTaskStatus}
                        onSelectTask={handleSelectTask}
                    />
                )}
                {currentView === 'calendar' && (
                    <CalendarView
                        tasks={filteredTasks}
                        taskRoles={taskRoles}
                        onSelectTask={handleSelectTask}
                    />
                )}
            </div>

            {/* Modals */}
            {selectedTask && (
                <TaskDetailModal
                    task={selectedTask}
                    isOpen={!!selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onUpdate={handleUpdateTask}
                    onDelete={() => setDeleteTaskModal({ isOpen: true, taskId: selectedTask._id, taskTitle: selectedTask.title })}
                    projectId={id}
                    projectTitle={project?.title}
                    users={users}
                    taskStatuses={taskStatuses}
                    sprints={sprints}
                    phases={phases}
                    taskRoles={taskRoles}
                    meetingNotes={meetingNotes}
                    requirements={requirements}
                />
            )}

            {assigneeModalTask && (
                <AssigneeModal
                    task={assigneeModalTask}
                    projectId={id}
                    users={users}
                    taskRoles={taskRoles}
                    onClose={() => setAssigneeModalTask(null)}
                    onUpdate={() => {
                        setAssigneeModalTask(null);
                        fetchProjectData();
                        if (onProjectRefresh) onProjectRefresh();
                    }}
                />
            )}

            <InlineTaskCreator
                isOpen={showInlineCreator}
                onClose={() => setShowInlineCreator(false)}
                onCreate={handleCreateTask}
                taskStatuses={taskStatuses}
                users={users}
                sprints={sprints}
                phases={phases}
                meetingNotes={meetingNotes}
                requirements={requirements}
            />

            <DeleteConfirmModal
                isOpen={deleteTaskModal.isOpen}
                onClose={() => setDeleteTaskModal({ isOpen: false })}
                onConfirm={handleDeleteTask}
                title="Delete Task"
                message={`Are you sure you want to permanently delete "${deleteTaskModal.taskTitle}"?`}
            />

            {showAutoScheduleModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[2000] p-6 animate-in fade-in">
                    <div className="bg-white rounded-3xl w-full max-w-sm shadow-xl border border-slate-200 overflow-hidden animate-in zoom-in-95">
                        <div className="p-6 bg-indigo-600 text-white">
                            <h3 className="text-xl font-bold">Auto Schedule</h3>
                            <p className="text-xs text-indigo-100 mt-1">Calculates each task's start &amp; due dates from durations, working hours, weekends, holidays and leaves — in task sequence.</p>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Start Date</label>
                                <input
                                    type="date"
                                    value={scheduleStartDate}
                                    onChange={e => setScheduleStartDate(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-indigo-400"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Target Role</label>
                                <select value={scheduleRoleId} onChange={e => setScheduleRoleId(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none focus:border-indigo-400">
                                    <option value="">Select a role</option>
                                    {taskRoles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                </select>
                                <p className="text-[10px] text-slate-400 leading-snug">
                                    Schedules this role across tasks; later roles never start before earlier roles in the same task end.
                                </p>
                            </div>
                            <div className="flex gap-3 pt-4 border-t border-slate-100">
                                <button onClick={() => setShowAutoScheduleModal(false)} className="flex-1 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
                                <button onClick={handleAutoSchedule} disabled={isAutoScheduling || !scheduleStartDate || !scheduleRoleId} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-bold text-xs shadow-md hover:bg-indigo-700 transition-all disabled:opacity-50">
                                    {isAutoScheduling ? 'Scheduling...' : 'Start Auto Sync'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <GenerateAITasksModal
                isOpen={showAITasksModal}
                onClose={() => setShowAITasksModal(false)}
                onGenerate={handleGenerateAITasksSubmit}
                projectTitle={project?.title}
                projectDescription={project?.description}
            />
        </div>
    );
};

export default TasksTab;
